import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BYBIT_ORDERBOOK_STREAM_DEPTH,
  BYBIT_ORDERBOOK_VISIBLE_LEVELS,
  DEFAULT_SAFE_SLIPPAGE_BPS,
  useBybitOrderbooks
} from '../../hooks/useBybitOrderbooks';

const getLocale = (language) => (language === 'ru' ? 'ru-RU' : 'en-US');

const getOrderbookCopy = (language) => {
  if (language === 'ru') {
    return {
      title: 'Стаканы Bybit',
      subtitle: 'Live-стаканы по двум монетам стратегии.',
      source: 'Bybit V5 WebSocket',
      streamDepth: (depth) => `${depth} уровней`,
      live: 'Live',
      connecting: 'Подключение',
      syncing: 'Синхронизация',
      reconnecting: 'Переподключение',
      errorStatus: 'Ошибка потока',
      updated: (time) => `Обновлено ${time}`,
      noSymbols: 'Символы стратегии пока не заполнены.',
      unavailable: 'Недоступно',
      error: 'Не удалось загрузить стакан Bybit.',
      bestBid: 'Bid',
      bestAsk: 'Ask',
      spread: 'Спред',
      safePosition: 'Без проскальзывания',
      entrySize: 'Вход',
      pressure: 'Давление на стакан',
      topLevel: '1-й уровень',
      topLevelHint: (qty, coin) => `${qty} ${coin}`,
      safePositionHint: (value) => `до ${value}%`,
      buyDepth: 'Buy',
      sellDepth: 'Sell',
      entryUnknown: 'Размер входа не задан',
      entryPerLegHint: (total) => `на монету из пары $${total}`,
      pressureSafe: 'Нормально',
      pressureRisky: 'Не рекомендуется',
      pressureNoLiquidity: 'Ликвидности не хватает',
      optimalPosition: 'Оптимальная позиция',
      askPrice: 'Ask px',
      askQty: 'Ask qty',
      bidQty: 'Bid qty',
      bidPrice: 'Bid px'
    };
  }

  return {
    title: 'Bybit Orderbooks',
    subtitle: 'Live orderbooks for both strategy symbols.',
    source: 'Bybit V5 WebSocket',
    streamDepth: (depth) => `${depth} levels`,
    live: 'Live',
    connecting: 'Connecting',
    syncing: 'Syncing',
    reconnecting: 'Reconnecting',
    errorStatus: 'Stream error',
    updated: (time) => `Updated ${time}`,
    noSymbols: 'Strategy symbols are not configured yet.',
    unavailable: 'Unavailable',
    error: 'Failed to load Bybit orderbook.',
    bestBid: 'Bid',
    bestAsk: 'Ask',
    spread: 'Spread',
    safePosition: 'No slippage',
    entrySize: 'Entry',
    pressure: 'Book pressure',
    topLevel: 'Top level',
    topLevelHint: (qty, coin) => `${qty} ${coin}`,
    safePositionHint: (value) => `up to ${value}%`,
    buyDepth: 'Buy',
    sellDepth: 'Sell',
    entryUnknown: 'Entry size is not set',
    entryPerLegHint: (total) => `per coin from $${total} pair`,
    pressureSafe: 'Normal',
    pressureRisky: 'Not recommended',
    pressureNoLiquidity: 'Insufficient liquidity',
    optimalPosition: 'Optimal position',
    askPrice: 'Ask px',
    askQty: 'Ask qty',
    bidQty: 'Bid qty',
    bidPrice: 'Bid px'
  };
};

const formatMoney = (value, language, maximumFractionDigits = 0) => {
  return new Intl.NumberFormat(getLocale(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits
  }).format(Number(value || 0));
};

const formatNumber = (value, language, maximumFractionDigits = 4) => {
  return new Intl.NumberFormat(getLocale(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits
  }).format(Number(value || 0));
};

const formatPercent = (value, language, maximumFractionDigits = 3) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }

  return `${formatNumber(value, language, maximumFractionDigits)}%`;
};

const getPressureTone = (item) => {
  const maxAllowedImpactPct = item.maxRecommendedSlippagePctWithTick || item.maxRecommendedSlippagePct;

  if (!item.strategyEntrySize) {
    return {
      bar: 'bg-slate-500',
      text: 'text-slate-300',
      badge: 'bg-slate-900 text-slate-300',
      border: 'border-slate-700/60'
    };
  }

  if (!item.hasEntryLiquidity || item.worstEntryImpactPct === null || !Number.isFinite(item.worstEntryImpactPct)) {
    return {
      bar: 'bg-rose-400',
      text: 'text-rose-300',
      badge: 'bg-rose-500/15 text-rose-100',
      border: 'border-rose-500/20'
    };
  }

  if (item.worstEntryImpactPct <= item.maxRecommendedSlippagePct * 0.5) {
    return {
      bar: 'bg-emerald-400',
      text: 'text-emerald-300',
      badge: 'bg-emerald-500/15 text-emerald-200',
      border: 'border-emerald-500/20'
    };
  }

  if (item.worstEntryImpactPct <= maxAllowedImpactPct) {
    return {
      bar: 'bg-amber-400',
      text: 'text-amber-300',
      badge: 'bg-amber-500/15 text-amber-100',
      border: 'border-amber-500/20'
    };
  }

  return {
    bar: 'bg-rose-400',
    text: 'text-rose-300',
    badge: 'bg-rose-500/15 text-rose-100',
    border: 'border-rose-500/20'
  };
};

const getConnectionMeta = (connectionState, copy) => {
  switch (connectionState) {
    case 'live':
      return {
        label: copy.live,
        className: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-200'
      };
    case 'syncing':
      return {
        label: copy.syncing,
        className: 'border-sky-500/20 bg-sky-500/10 text-sky-200'
      };
    case 'reconnecting':
      return {
        label: copy.reconnecting,
        className: 'border-amber-500/20 bg-amber-500/10 text-amber-100'
      };
    case 'connecting':
      return {
        label: copy.connecting,
        className: 'border-slate-700/80 bg-slate-900/70 text-slate-200'
      };
    case 'error':
      return {
        label: copy.errorStatus,
        className: 'border-rose-500/20 bg-rose-500/10 text-rose-100'
      };
    default:
      return {
        label: copy.connecting,
        className: 'border-slate-700/80 bg-slate-900/70 text-slate-200'
      };
  }
};

const MiniStat = ({
  label,
  value,
  subValue,
  tone = 'text-white',
  valueClassName = 'text-sm',
  subValueClassName = 'text-[10px] text-slate-400',
  className = ''
}) => (
  <div className={`rounded-lg border border-slate-700/60 bg-slate-950/55 px-2.5 py-2 ${className}`}>
    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</div>
    <div className={`mt-1 font-bold ${valueClassName} ${tone}`}>{value}</div>
    {subValue ? <div className={`mt-0.5 ${subValueClassName}`}>{subValue}</div> : null}
  </div>
);

const SkeletonCard = () => (
  <div className="rounded-xl border border-slate-700/60 bg-slate-900/55 p-3 animate-pulse">
    <div className="flex items-start justify-between gap-2">
      <div className="h-5 w-28 rounded bg-slate-700/70" />
      <div className="grid grid-cols-2 gap-2">
        <div className="h-10 w-20 rounded-lg bg-slate-800/90" />
        <div className="h-10 w-20 rounded-lg bg-slate-800/90" />
      </div>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      <div className="h-12 rounded-lg bg-slate-800/80" />
      <div className="h-12 rounded-lg bg-slate-800/80" />
      <div className="h-12 rounded-lg bg-slate-800/80" />
    </div>
    <div className="mt-2 h-8 rounded-lg bg-slate-800/80" />
    <div className="mt-2 h-20 rounded-lg bg-slate-800/80" />
  </div>
);

const DepthCell = ({ side, value, ratio, align = 'left', tone }) => {
  if (!value) {
    return <div className={`rounded-md px-2 py-1 text-[10px] text-slate-500 ${align === 'right' ? 'text-right' : ''}`}>—</div>;
  }

  const fillClass = side === 'ask' ? 'bg-rose-500/15' : 'bg-emerald-500/15';
  const positionClass = side === 'ask' ? 'left-0' : 'right-0';

  return (
    <div className={`relative overflow-hidden rounded-md px-2 py-1 text-[10px] ${align === 'right' ? 'text-right' : ''}`}>
      <div className={`absolute inset-y-0 ${positionClass} ${fillClass}`} style={{ width: `${ratio}%` }} />
      <div className={`relative z-10 font-mono ${tone}`}>{value}</div>
    </div>
  );
};

const CombinedOrderbook = ({ item, language, copy }) => {
  const rows = Array.from({ length: BYBIT_ORDERBOOK_VISIBLE_LEVELS }, (_, index) => ({
    ask: item.asks[index] || null,
    bid: item.bids[index] || null
  }));

  const maxAskDepth = item.asks[Math.min(item.asks.length, BYBIT_ORDERBOOK_VISIBLE_LEVELS) - 1]?.cumulativeNotional || 0;
  const maxBidDepth = item.bids[Math.min(item.bids.length, BYBIT_ORDERBOOK_VISIBLE_LEVELS) - 1]?.cumulativeNotional || 0;

  return (
    <div className="mt-2 rounded-lg border border-slate-700/60 bg-slate-950/55 p-2">
      <div className="grid grid-cols-[1fr_0.95fr_0.95fr_1fr] gap-1 px-2 pb-1.5 text-[9px] uppercase tracking-[0.16em] text-slate-500">
        <div>{copy.askPrice}</div>
        <div>{copy.askQty}</div>
        <div className="text-right">{copy.bidQty}</div>
        <div className="text-right">{copy.bidPrice}</div>
      </div>

      <div className="space-y-0.5">
        {rows.map((row, index) => {
          const askRatio = row.ask && maxAskDepth ? (row.ask.cumulativeNotional / maxAskDepth) * 100 : 0;
          const bidRatio = row.bid && maxBidDepth ? (row.bid.cumulativeNotional / maxBidDepth) * 100 : 0;

          return (
            <div key={`${item.symbol}-${index}`} className="grid grid-cols-[1fr_0.95fr_0.95fr_1fr] gap-1 rounded-md bg-slate-900/55">
              <DepthCell
                side="ask"
                value={row.ask ? formatNumber(row.ask.price, language, item.pricePrecision) : ''}
                ratio={askRatio}
                tone="text-rose-300"
              />
              <DepthCell
                side="ask"
                value={row.ask ? formatNumber(row.ask.size, language, item.qtyPrecision) : ''}
                ratio={askRatio}
                tone="text-slate-200"
              />
              <DepthCell
                side="bid"
                value={row.bid ? formatNumber(row.bid.size, language, item.qtyPrecision) : ''}
                ratio={bidRatio}
                align="right"
                tone="text-slate-200"
              />
              <DepthCell
                side="bid"
                value={row.bid ? formatNumber(row.bid.price, language, item.pricePrecision) : ''}
                ratio={bidRatio}
                align="right"
                tone="text-emerald-300"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OrderbookCard = ({ item, language, copy, strategyEntrySizeTotal }) => {
  if (item.status === 'loading') {
    return <SkeletonCard />;
  }

  if (item.status === 'error') {
    return (
      <div className="rounded-xl border border-rose-500/20 bg-slate-900/55 p-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-white">{item.symbol}</h4>
          <span className="rounded-full border border-rose-500/20 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-200">
            {copy.unavailable}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-300">{item.error || copy.error}</p>
      </div>
    );
  }

  const baseCoin = item.baseCoin || item.symbol.replace(item.quoteCoin || 'USDT', '');
  const pressureTone = getPressureTone(item);
  const pressureLabel = !item.strategyEntrySize
    ? copy.entryUnknown
    : item.isEntryRecommended
      ? copy.pressureSafe
      : !item.hasEntryLiquidity
        ? copy.pressureNoLiquidity
        : copy.pressureRisky;
  const entryImpactHint = !item.strategyEntrySize
    ? copy.entryUnknown
    : item.hasEntryLiquidity
      ? `${copy.entryPerLegHint(formatMoney(strategyEntrySizeTotal, language))} · ${copy.buyDepth}: ${formatPercent(item.buyEntryImpact?.slippagePct, language)} · ${copy.sellDepth}: ${formatPercent(item.sellEntryImpact?.slippagePct, language)}`
      : copy.pressureNoLiquidity;
  const pressureValue = !item.strategyEntrySize
    ? '—'
    : !item.hasEntryLiquidity || item.worstEntryImpactPct === null
      ? `>${formatPercent(item.maxRecommendedSlippagePct, language, 2)}`
      : formatPercent(item.worstEntryImpactPct, language);
  const pressureProgress = item.strategyEntrySize
    ? !item.hasEntryLiquidity || item.worstEntryImpactPct === null
      ? 100
      : Math.min((item.worstEntryImpactPct / (item.maxRecommendedSlippagePctWithTick || item.maxRecommendedSlippagePct)) * 100, 100)
    : 0;
  const optimalPositionValue = item.stableOptimalPositionNotional > 0
    ? `$${formatMoney(item.stableOptimalPositionNotional, language)}`
    : '—';
  const optimalPositionHint = item.stableOptimalPositionQty > 0
    ? `${formatNumber(item.stableOptimalPositionQty, language, item.qtyPrecision)} ${baseCoin} · ${copy.safePositionHint(formatNumber(item.maxRecommendedSlippagePct, language, 2))}`
    : null;

  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-900/55 p-3">
      <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0 px-1 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-[15px] font-semibold leading-none text-white">{item.symbol}</h4>
            <span className="rounded-full border border-blue-500/20 bg-blue-500/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-blue-200">
              {item.marketLabel}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 xl:min-w-[170px]">
          <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/10 px-2.5 py-1.5">
            <div className="text-[9px] uppercase tracking-[0.16em] text-emerald-200/70">{copy.bestBid}</div>
            <div className="mt-0.5 text-xs font-bold text-emerald-200">
              {formatNumber(item.bestBid, language, item.pricePrecision)}
            </div>
          </div>
          <div className="rounded-lg border border-rose-500/15 bg-rose-500/10 px-2.5 py-1.5">
            <div className="text-[9px] uppercase tracking-[0.16em] text-rose-200/70">{copy.bestAsk}</div>
            <div className="mt-0.5 text-xs font-bold text-rose-200">
              {formatNumber(item.bestAsk, language, item.pricePrecision)}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[0.95fr_1.15fr_0.95fr]">
        <MiniStat
          label={copy.spread}
          value={`${formatNumber(item.spread, language, item.pricePrecision)} (${formatNumber(item.spreadPct, language, 3)}%)`}
          subValue={item.spreadTicks > 0 ? `${formatNumber(item.spreadTicks, language, 1)} ticks` : null}
          tone="text-sky-300"
        />
        <MiniStat
          label={copy.optimalPosition}
          value={optimalPositionValue}
          subValue={optimalPositionHint}
          tone="text-cyan-300"
          valueClassName="text-xl leading-none sm:text-2xl"
          subValueClassName="text-[11px] text-slate-300"
          className="border-cyan-500/20 bg-cyan-500/5"
        />
        <MiniStat
          label={copy.entrySize}
          value={item.strategyEntrySize ? `$${formatMoney(item.strategyEntrySize, language)}` : '—'}
          subValue={entryImpactHint}
          tone={item.strategyEntrySize && !item.isEntryRecommended ? 'text-rose-200' : 'text-white'}
        />
      </div>

      <div className={`mt-2 rounded-lg border ${pressureTone.border} bg-slate-950/55 px-2.5 py-1.5`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${pressureTone.badge}`}>{pressureLabel}</span>
            <span className="text-[9px] uppercase tracking-[0.16em] text-slate-500">{copy.pressure}</span>
          </div>
          <div className={`text-xs font-semibold ${pressureTone.text}`}>
            {pressureValue}
          </div>
        </div>
        {item.strategyEntrySize ? (
          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-slate-800">
            <div className={`h-full rounded-full ${pressureTone.bar}`} style={{ width: `${pressureProgress}%` }} />
          </div>
        ) : null}
      </div>

      <CombinedOrderbook item={item} language={language} copy={copy} />
    </div>
  );
};

const StrategyOrderbooksPanel = ({ strategyParams, isVisible = true }) => {
  const { i18n } = useTranslation();
  const copy = useMemo(() => getOrderbookCopy(i18n.language), [i18n.language]);

  const symbols = useMemo(
    () => [strategyParams?.symbol_a, strategyParams?.symbol_b].filter(Boolean),
    [strategyParams]
  );
  const strategyEntrySize = useMemo(() => {
    const value = Number(strategyParams?.total_usdt_one_entry);
    return Number.isFinite(value) ? value : 0;
  }, [strategyParams]);
  const strategyEntrySizePerLeg = useMemo(() => {
    return strategyEntrySize > 0 ? strategyEntrySize / 2 : 0;
  }, [strategyEntrySize]);

  const { items, isLoading, connectionState, error } = useBybitOrderbooks({
    symbols,
    strategyEntrySize: strategyEntrySizePerLeg,
    depth: BYBIT_ORDERBOOK_STREAM_DEPTH,
    slippageBps: DEFAULT_SAFE_SLIPPAGE_BPS
  });

  const liveMeta = getConnectionMeta(connectionState, copy);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800 p-4 shadow-lg">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-white">{copy.title}</h3>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${liveMeta.className}`}>
          {liveMeta.label}
        </span>
      </div>

      {!symbols.length ? (
        <div className="mt-3 rounded-xl border border-dashed border-slate-700 bg-slate-900/40 px-5 py-6 text-center text-sm text-slate-400">
          {copy.noSymbols}
        </div>
      ) : null}

      {error && !items.some((item) => item.status === 'ready') ? (
        <div className="mt-3 rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {error}
        </div>
      ) : null}

      {symbols.length ? (
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {isLoading && !items.some((item) => item.status === 'ready')
            ? symbols.map((symbol) => <SkeletonCard key={symbol} />)
            : items.map((item) => (
                <OrderbookCard
                  key={item.symbol}
                  item={item}
                  language={i18n.language}
                  copy={copy}
                  strategyEntrySizeTotal={strategyEntrySize}
                />
              ))}
        </div>
      ) : null}
    </div>
  );
};

export default StrategyOrderbooksPanel;
